import React, {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
} from "react";
import { FormattedMessage } from "react-intl";
import {
  DrugCostInfo,
  DrugFootnote,
  DrugInfo,
  PrescriptionDrug,
} from "../../../@types";
import { AppStore } from "../../../app/store";
import {
  Footnote,
  FootnoteMarker,
  Footnotes,
  RenderedTable,
  TooltipWithClose,
} from "../../../components";
import { displayDrug, useTableConfig, useTranslate } from "../../../helpers";

interface OtherDrugInfoFoonote {
  text: string;
  type: DrugFootnote;
}

export const OtherDrugInformationTable: FunctionComponent<{
  drugCostInfo: DrugCostInfo;
  drugInfo: DrugInfo[];
  prescriptions: PrescriptionDrug[];
}> = ({ drugCostInfo, drugInfo, prescriptions }) => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppStore.AppContext);
  const [footnotes, setFootnotes] = useState<OtherDrugInfoFoonote[]>([]);
  const [ndcFootnoteHashMap, setNdcFootnoteHashMap] = useState({});

  const matchNote = (textToMatch: string) => (note: OtherDrugInfoFoonote) =>
    note.text === textToMatch;

  useEffect(() => {
    const { restrictions } = drugCostInfo;

    const conditions = restrictions.filter(r => !!r.conditions.length);
    const opioids = restrictions.filter(r => r.opioid);
    const homeInfusions = restrictions.filter(r => r.home_infusion);
    const limitedDistros = restrictions.filter(r => r.limited_distribution);
    const vaccines = restrictions.filter(r => r.vaccine);

    const newNdcFootnoteHashMap = {};
    const notes: { text: string; type: DrugFootnote }[] = [];

    conditions.forEach(c => {
      const conditionText =
        t("plan_details.drug_restrictions") + " " + c.conditions.join("; ");
      notes.push({
        text: conditionText,
        type: DrugFootnote.CONDITION,
      });
      const matchedIndex = notes.findIndex(matchNote(conditionText));
      newNdcFootnoteHashMap[c.ndc]
        ? newNdcFootnoteHashMap[c.ndc].push(matchedIndex)
        : (newNdcFootnoteHashMap[c.ndc] = [matchedIndex]);
    });

    if (opioids.length) {
      const opioidText = t("plan_details.opioid_footnote");
      notes.push({
        text: opioidText,
        type: DrugFootnote.OPIOID,
      });
      const matchedIndex = notes.findIndex(matchNote(opioidText));
      opioids.forEach(opiod => {
        newNdcFootnoteHashMap[opiod.ndc]
          ? newNdcFootnoteHashMap[opiod.ndc].push(matchedIndex)
          : (newNdcFootnoteHashMap[opiod.ndc] = [matchedIndex]);
      });
    }

    if (homeInfusions.length) {
      const homeInfusionText = t("drug_footnote.home_infusion");
      notes.push({
        text: homeInfusionText,
        type: DrugFootnote.HOME_INFUSION,
      });
      const matchedIndex = notes.findIndex(matchNote(homeInfusionText));
      homeInfusions.forEach(homeInfusion => {
        newNdcFootnoteHashMap[homeInfusion.ndc]
          ? newNdcFootnoteHashMap[homeInfusion.ndc].push(matchedIndex)
          : (newNdcFootnoteHashMap[homeInfusion.ndc] = [matchedIndex]);
      });
    }

    if (limitedDistros.length) {
      const limitedDistrosText = t("drug_footnote.limited_distribution");
      notes.push({
        text: limitedDistrosText,
        type: DrugFootnote.LIMITED_DISTRIBUTION,
      });
      const matchedIndex = notes.findIndex(matchNote(limitedDistrosText));
      limitedDistros.forEach(limitedDistro => {
        newNdcFootnoteHashMap[limitedDistro.ndc]
          ? newNdcFootnoteHashMap[limitedDistro.ndc].push(matchedIndex)
          : (newNdcFootnoteHashMap[limitedDistro.ndc] = [matchedIndex]);
      });
    }

    if (vaccines.length) {
      const vaccinesText = t("drug_footnote.vaccine");
      notes.push({
        text: vaccinesText,
        type: DrugFootnote.VACCINE,
      });
      const matchedIndex = notes.findIndex(matchNote(vaccinesText));
      vaccines.forEach(vaccine => {
        newNdcFootnoteHashMap[vaccine.ndc]
          ? newNdcFootnoteHashMap[vaccine.ndc].push(matchedIndex)
          : (newNdcFootnoteHashMap[vaccine.ndc] = [matchedIndex]);
      });
    }
    setNdcFootnoteHashMap(newNdcFootnoteHashMap);
    setFootnotes(notes);
  }, [drugCostInfo, language]);

  const drugData = prescriptions.reduce(
    (
      accumulator: Record<string, unknown>[],
      prescription: PrescriptionDrug
    ) => {
      const drug = drugInfo.find(d => d.ndc === prescription.ndc);
      if (drug) {
        const tooltipContent = (
          <div
            className="OtherDrugInfo__tooltipContent"
            id="quantity-limits-tooltip"
            role="tooltip"
          >
            <span className="ds-u-font-weight--bold">
              {t("plan_terms.quantity_limits_amount")}
            </span>

            <span className="ds-u-margin-bottom--1">
              {drug.quantity_limit ? drug.quantity_limit_amount : ""}
            </span>
            <span className="ds-u-font-weight--bold">
              {t("plan_terms.quantity_limits_frequency")}
            </span>
            <span>
              {drug.quantity_limit
                ? `${drug.quantity_limit_days} ${t("days")}`
                : ""}
            </span>
          </div>
        );
        accumulator.push({
          drugName: (
            <span className="mct-c-table__header-title">
              {displayDrug(t, prescription)}
              {ndcFootnoteHashMap[drug.ndc] &&
                ndcFootnoteHashMap[drug.ndc].map((footnoteIndex: number) => (
                  <FootnoteMarker
                    key={`footnoteIndex-${footnoteIndex}`}
                    id={`footnote-${footnoteIndex}`}
                    marker={(footnoteIndex + 1).toString()}
                  />
                ))}
            </span>
          ),
          tier:
            drug.tier > 0 ? (
              <FormattedMessage
                id="plan_terms.tier-number"
                tagName="span"
                values={{ tier: drug.tier }}
              />
            ) : (
              t("plan_terms.not_covered")
            ),
          priorAuthorization: drug.prior_auth ? t("yes") : <span>&#8212;</span>,
          quantityLimits: drug.quantity_limit ? (
            <TooltipWithClose title={tooltipContent} headerText="">
              {t("yes")}
            </TooltipWithClose>
          ) : (
            <span>&#8212;</span>
          ),
          stepTherapy: drug.step_therapy ? t("yes") : <span>&#8212;</span>,
        });
      }
      return accumulator;
    },
    []
  );
  const data = useTableConfig(drugData, [
    drugInfo,
    drugCostInfo,
    ndcFootnoteHashMap,
    t,
  ]);

  const footnotesElem = footnotes.length ? (
    <Footnotes>
      {footnotes.map((footnote, index) => {
        return (
          <Footnote
            id={`footnote-${index}`}
            className="ds-u-measure--wide ds-u-margin--0"
            text={
              <span className={`e2e-other-footnote-${footnote.type}`}>
                {footnote.text}
              </span>
            }
            marker={(index + 1).toString()}
            key={index}
          />
        );
      })}
    </Footnotes>
  ) : null;

  const columns = useTableConfig(
    [
      {
        accessor: "drugName",
        Footer: footnotesElem,
      },
      {
        Header: t("plan_terms.tier"),
        accessor: "tier",
      },
      {
        Header: t("plan_terms.prior_authorization"),
        accessor: "priorAuthorization",
      },
      {
        Header: t("plan_terms.quantity_limits"),
        accessor: "quantityLimits",
      },
      {
        Header: t("plan_terms.step_therapy"),
        accessor: "stepTherapy",
      },
    ],
    [drugInfo, t]
  );

  return (
    <RenderedTable
      id="OtherDrugInformation"
      tableColumns={columns}
      tableData={data}
      caption={t("plan_terms.other_drug_information")}
      className={footnotes.length ? "has-footnotes" : ""}
      borderless
      rowHeaders
      stackableBreakpoint="lg"
    />
  );
};
